import type { MapProvider } from '@gpx-spa/geo-domain';
import type { Ref } from 'vue';
import { onBeforeUnmount, watch } from 'vue';

export type OperationalZoomVisibilityRule = {
  layerId: string;
  /** Inclusive; omit for no lower bound. */
  minZoom?: number;
  /** Inclusive; omit for no upper bound. */
  maxZoom?: number;
};

function zoomMatchesRule(
  z: number,
  rule: OperationalZoomVisibilityRule,
): boolean {
  const min = rule.minZoom ?? -Infinity;
  const max = rule.maxZoom ?? Infinity;
  return z >= min && z <= max;
}

/**
 * Shows or hides registered {@link MapProvider} layers based on current zoom.
 * When `rules` is empty, nothing is toggled (all layers keep their last visibility).
 */
export function useOperationalMapLayerVisibility(
  mapProvider: () => MapProvider | undefined,
  mapReady: Ref<boolean>,
  rules: Ref<OperationalZoomVisibilityRule[]>,
): void {
  let unsubMove: (() => void) | undefined;

  function apply() {
    const p = mapProvider();
    const list = rules.value;
    if (!p || list.length === 0) return;
    const z = p.getZoom();
    if (z === undefined) return;
    for (const rule of list) {
      const layer = p.getLayer(rule.layerId);
      if (!layer) continue;
      if (zoomMatchesRule(z, rule)) {
        layer.show();
      } else {
        layer.hide();
      }
    }
  }

  function teardownMove() {
    unsubMove?.();
    unsubMove = undefined;
  }

  watch(
    () => mapReady.value,
    (ready) => {
      teardownMove();
      if (!ready) return;
      const p = mapProvider();
      if (!p) return;
      apply();
      unsubMove = p.onMoveEnd(apply);
    },
    { immediate: true },
  );

  watch(
    () => rules.value,
    () => {
      apply();
    },
    { deep: true },
  );

  onBeforeUnmount(() => {
    teardownMove();
  });
}
